import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Loader2, Play, Zap, Trash2, Plus, Eye, Copy, PlayCircle } from 'lucide-react'
import { IAPISimulationSuite, IAPISimulationItem, IAPISimulationItemRun } from 'extra/types/ApiModels'
import { api } from '../../api/client'
import { useEntities } from '../../context/EntitiesContext'
import { useMultiSelect } from '../../hooks/useMultiSelect'
import { createProcessQueue } from '../../utils/processQueue'
import { SimulationItemEditor } from './SimulationItemEditor'
import { SimulationConversationViewer } from './SimulationConversationViewer'

function getRunsForTarget (item: IAPISimulationItem, targetId: string): Array<IAPISimulationItemRun> {
  return (item.runs ?? []).filter(r => r.targetId === targetId).sort((a, b) => a.runIndex - b.runIndex)
}

function updateItemRun (items: Array<IAPISimulationItem>, itemId: string, run: IAPISimulationItemRun): Array<IAPISimulationItem> {
  return items.map(item => {
    if (item.id !== itemId) return item
    const runs = item.runs ?? []
    const existingIndex = runs.findIndex(r => r.id === run.id)
    const updatedRuns = existingIndex >= 0
      ? runs.map((r, i) => i === existingIndex ? run : r)
      : [...runs, run]
    return { ...item, runs: updatedRuns }
  })
}

function clearItemRuns (items: Array<IAPISimulationItem>, opts?: { itemIds?: Array<string>, targetIds?: Array<string> }): Array<IAPISimulationItem> {
  return items.map(item => {
    if (opts?.itemIds && !opts.itemIds.includes(item.id)) return item
    if (!opts?.targetIds) return { ...item, runs: [] }
    return { ...item, runs: (item.runs ?? []).filter(r => !opts.targetIds!.includes(r.targetId)) }
  })
}

export function SimulationItems ({ suite }: { suite: IAPISimulationSuite }) {
  const { variants: allVariants, deployments: allDeployments } = useEntities()
  const [items, setItems] = useState<Array<IAPISimulationItem>>([])
  const [loading, setLoading] = useState(true)
  const [editingItem, setEditingItem] = useState<IAPISimulationItem | 'new' | null>(null)
  const [viewingConversation, setViewingConversation] = useState<{
    item: IAPISimulationItem
    targetId: string
    initialRunIndex: number
  } | null>(null)

  const variants = useMemo(() => allVariants.filter(v => v.promptId === suite.promptId), [allVariants, suite.promptId])
  const deployments = useMemo(() => allDeployments.filter(d => d.promptId === suite.promptId), [allDeployments, suite.promptId])

  const { selectedIds, setSelectedIds, toggleSelect, toggleSelectAll } = useMultiSelect()
  const [executingIds, setExecutingIds] = useState<Set<string>>(new Set())
  const [evaluatingIds, setEvaluatingIds] = useState<Set<string>>(new Set())

  const queueRef = useRef(createProcessQueue<IAPISimulationItemRun>({
    concurrency: 10,
    onItemComplete: (_taskId, run) => {
      setItems(prev => updateItemRun(prev, run.simulationItemId, run))
    },
    onItemError: (taskId, e) => {
      console.error(`Failed to process ${taskId}`, e)
    },
    onItemFinally: (taskId) => {
      setExecutingIds(prev => {
        if (!prev.has(taskId)) return prev
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
      setEvaluatingIds(prev => {
        if (!prev.has(taskId)) return prev
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
    },
  }))

  useEffect(() => {
    setLoading(true)
    api.simulations.listItems(suite.id)
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [suite.id])

  const associatedIds = useMemo(() => [
    ...(suite.promptVariantIds || []).filter(id => variants.some(v => v.id === id)),
    ...(suite.deploymentIds || []).filter(id => deployments.some(d => d.id === id)),
  ], [suite.promptVariantIds, suite.deploymentIds, variants, deployments])

  const getTargetName = useCallback((id: string) => {
    const variant = variants.find(v => v.id === id)
    if (variant) return variant.name
    const deployment = deployments.find(d => d.id === id)
    if (deployment) return deployment.label || id.slice(0, 8)
    return id.slice(0, 8)
  }, [variants, deployments])

  const getTargetType = useCallback((id: string): 'variant' | 'deployment' => {
    if (suite.promptVariantIds?.includes(id)) return 'variant'
    return 'deployment'
  }, [suite.promptVariantIds])

  const executeItems = async (itemIds: Array<string>) => {
    await api.simulations.cleanup(suite.id, { itemIds })
    setItems(prev => clearItemRuns(prev, { itemIds }))

    const taskIds: Array<string> = []
    for (const itemId of itemIds) {
      for (const targetId of associatedIds) {
        const targetType = getTargetType(targetId)
        for (let runIndex = 0; runIndex < suite.runsPerItem; runIndex++) {
          taskIds.push(`${itemId}::${targetId}::${runIndex}::${targetType}`)
        }
      }
    }

    setExecutingIds(prev => {
      const next = new Set(prev)
      taskIds.forEach(id => next.add(id))
      return next
    })

    queueRef.current.add(taskIds, async (taskId) => {
      const [itemId, targetId, runIndexStr, targetType] = taskId.split('::')
      return api.simulations.executeItem(itemId, {
        targetId,
        targetType: targetType as 'variant' | 'deployment',
        runIndex: parseInt(runIndexStr, 10),
      })
    })
  }

  const evaluateItems = (itemIds: Array<string>) => {
    const runIds: Array<string> = []
    for (const itemId of itemIds) {
      const item = items.find(i => i.id === itemId)
      if (!item?.runs) continue
      for (const run of item.runs) {
        if (run.result && run.result.status === 'completed') {
          runIds.push(run.id)
        }
      }
    }

    setEvaluatingIds(prev => {
      const next = new Set(prev)
      runIds.forEach(id => next.add(id))
      return next
    })

    queueRef.current.add(runIds, (runId) => api.simulations.evaluateRun(runId))
  }

  const executeAndEvaluateItems = async (itemIds: Array<string>) => {
    await api.simulations.cleanup(suite.id, { itemIds })
    setItems(prev => clearItemRuns(prev, { itemIds }))

    const taskIds: Array<string> = []
    for (const itemId of itemIds) {
      for (const targetId of associatedIds) {
        const targetType = getTargetType(targetId)
        for (let runIndex = 0; runIndex < suite.runsPerItem; runIndex++) {
          taskIds.push(`${itemId}::${targetId}::${runIndex}::${targetType}`)
        }
      }
    }

    setExecutingIds(prev => {
      const next = new Set(prev)
      taskIds.forEach(id => next.add(id))
      return next
    })

    queueRef.current.add(taskIds, async (taskId) => {
      const [itemId, targetId, runIndexStr, targetType] = taskId.split('::')
      const run = await api.simulations.executeItem(itemId, {
        targetId,
        targetType: targetType as 'variant' | 'deployment',
        runIndex: parseInt(runIndexStr, 10),
      })
      setItems(prev => updateItemRun(prev, itemId, run))
      if (run.result && run.result.status === 'completed') {
        const evaluatedRun = await api.simulations.evaluateRun(run.id)
        return evaluatedRun
      }
      return run
    })
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return
    if (!window.confirm(`Delete ${selectedIds.length} items?`)) return
    try {
      await api.simulations.deleteItems(selectedIds)
      setItems(prev => prev.filter(i => !selectedIds.includes(i.id)))
      setSelectedIds([])
    } catch (e) {
      console.error('Failed to delete items', e)
    }
  }

  const handleDuplicateItem = async (itemId: string) => {
    try {
      const duplicated = await api.simulations.duplicateItem(itemId)
      setItems(prev => [duplicated, ...prev])
    } catch (e) {
      console.error('Failed to duplicate item', e)
    }
  }

  const handleItemSaved = (item: IAPISimulationItem) => {
    setItems(prev => {
      const exists = prev.find(i => i.id === item.id)
      if (exists) return prev.map(i => i.id === item.id ? item : i)
      return [item, ...prev]
    })
    setEditingItem(null)
  }

  const getScoreColor = (score: number) => {
    const hue = score * 120
    return `hsl(${hue}, 80%, 60%)`
  }

  const getRunStats = (item: IAPISimulationItem, targetId: string) => {
    const runs = getRunsForTarget(item, targetId)
    if (runs.length === 0) return null

    const completedRuns = runs.filter(r => r.result && r.result.status === 'completed')
    if (completedRuns.length === 0) return null

    const avgTurns = completedRuns.reduce((s, r) => s + r.result!.turnCount, 0) / completedRuns.length
    const avgLatency = completedRuns.reduce((s, r) => s + (r.metrics?.latency ?? 0), 0) / completedRuns.length

    const evalsWithScore = runs.filter(r => r.evalResult && typeof r.evalResult.score === 'number')
    let avgScore: number | null = null
    if (evalsWithScore.length > 0) {
      avgScore = evalsWithScore.reduce((s, r) => s + r.evalResult!.score, 0) / evalsWithScore.length
    }

    return {
      completed: completedRuns.length,
      total: runs.length,
      avgTurns: avgTurns.toFixed(1),
      avgLatency: (avgLatency / 1000).toFixed(1) + 's',
      avgScore,
    }
  }

  if (loading) return <div className="text-gray-400">Loading items...</div>

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 bg-gray-800/50 p-2 rounded-lg border border-gray-700 flex-wrap">
        <button
          onClick={() => setEditingItem('new')}
          className="btn btn-secondary text-sm flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </button>
        <button
          onClick={() => executeItems(selectedIds)}
          disabled={selectedIds.length === 0 || associatedIds.length === 0}
          className="btn btn-primary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play className="w-4 h-4" />
          Execute Selected ({selectedIds.length})
        </button>
        <button
          onClick={() => executeItems(items.map(i => i.id))}
          disabled={associatedIds.length === 0}
          className="btn btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play className="w-4 h-4" />
          Execute All
        </button>
        <button
          onClick={() => evaluateItems(selectedIds)}
          disabled={selectedIds.length === 0}
          className="btn btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ml-4"
        >
          <Zap className="w-4 h-4" />
          Evaluate Selected ({selectedIds.length})
        </button>
        <button
          onClick={() => evaluateItems(items.map(i => i.id))}
          className="btn btn-secondary text-sm flex items-center gap-2"
        >
          <Zap className="w-4 h-4" />
          Evaluate All
        </button>
        <button
          onClick={() => executeAndEvaluateItems(selectedIds)}
          disabled={selectedIds.length === 0 || associatedIds.length === 0}
          className="btn btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ml-4"
        >
          <PlayCircle className="w-4 h-4" />
          Exec+Eval Selected ({selectedIds.length})
        </button>
        <button
          onClick={() => executeAndEvaluateItems(items.map(i => i.id))}
          disabled={associatedIds.length === 0}
          className="btn btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlayCircle className="w-4 h-4" />
          Exec+Eval All
        </button>
        {selectedIds.length > 0 && (
          <button
            onClick={handleDeleteSelected}
            className="btn btn-danger text-sm flex items-center gap-2 ml-4"
          >
            <Trash2 className="w-4 h-4" />
            Delete ({selectedIds.length})
          </button>
        )}
        <div className="ml-auto text-sm text-gray-400 flex gap-4">
          {executingIds.size > 0 && (
            <span className="flex items-center gap-2 text-blue-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Executing...
            </span>
          )}
          {evaluatingIds.size > 0 && (
            <span className="flex items-center gap-2 text-yellow-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Evaluating...
            </span>
          )}
        </div>
      </div>

      {items.length === 0 && (
        <div className="text-gray-500">No items yet. Click "Add Item" to create one.</div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto border border-gray-700 rounded-lg">
          <table className="w-full text-left text-sm border-collapse">
            <thead className="bg-gray-800 sticky top-0 z-10">
              <tr>
                <th className="p-2 border-b border-gray-700 w-8 text-center">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selectedIds.length === items.length}
                    onChange={() => toggleSelectAll(items)}
                  />
                </th>
                <th className="p-2 border-b border-gray-700 font-medium text-gray-400">Title</th>
                <th className="p-2 border-b border-gray-700 font-medium text-gray-400">Driver Instructions</th>
                <th className="p-2 border-b border-gray-700 font-medium text-gray-400">Eval Criteria</th>
                {associatedIds.map(id => (
                  <th key={id} className="p-2 border-b border-gray-700 font-medium text-center border-l border-gray-600">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xs text-gray-500">
                        {getTargetType(id) === 'variant' ? 'V' : 'D'}:
                      </span>
                      <span>{getTargetName(id)}</span>
                      <button
                        onClick={() => executeItems(items.map(i => i.id))}
                        title="Execute all for this target"
                        className="text-gray-400 hover:text-blue-400 cursor-pointer"
                      >
                        <Play className="w-3 h-3" />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {items.map(item => (
                <tr
                  key={item.id}
                  className={`hover:bg-gray-800/50 transition-colors ${selectedIds.includes(item.id) ? 'bg-blue-900/30' : ''}`}
                >
                  <td className="p-2 align-top text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleSelect(item.id)}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingItem(item)}
                        className="text-blue-400 hover:text-blue-300 text-left cursor-pointer"
                      >
                        {item.title || item.id.slice(-8)}
                      </button>
                      <button
                        onClick={() => handleDuplicateItem(item.id)}
                        title="Duplicate item"
                        className="text-gray-500 hover:text-gray-300 cursor-pointer"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      {executingIds.size > 0 && Array.from(executingIds).some(id => id.startsWith(item.id)) && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
                      {evaluatingIds.size > 0 && (item.runs ?? []).some(r => evaluatingIds.has(r.id)) && <Loader2 className="w-3 h-3 animate-spin text-yellow-500" />}
                    </div>
                  </td>
                  <td className="p-2 align-top text-xs text-gray-300 max-w-48 truncate" title={item.driverInstructions || ''}>
                    {item.driverInstructions ? <>{item.driverInstructions.slice(0, 80)}{item.driverInstructions.length > 80 ? '...' : ''}</> : <span className="text-gray-600">-</span>}
                  </td>
                  <td className="p-2 align-top text-xs text-gray-400">
                    {item.evalCriteria
                      ? `${item.evalCriteria.critical?.length || 0}C / ${item.evalCriteria.niceToHave?.length || 0}N`
                      : '-'}
                  </td>
                  {associatedIds.map(targetId => {
                    const stats = getRunStats(item, targetId)
                    return (
                      <td key={targetId} className="p-2 align-top border-l border-gray-700 text-xs">
                        {stats
                          ? (
                          <div className="space-y-1">
                            <div className="text-gray-400">
                              {stats.completed}/{stats.total} runs
                            </div>
                            <div className="text-gray-400">
                              ~{stats.avgTurns} turns, {stats.avgLatency}
                            </div>
                            {stats.avgScore !== null && (
                              <div className="font-bold" style={{ color: getScoreColor(stats.avgScore) }}>
                                Score: {stats.avgScore.toFixed(2)}
                              </div>
                            )}
                            <button
                              onClick={() => setViewingConversation({ item, targetId, initialRunIndex: 0 })}
                              className="text-blue-400 hover:text-blue-300 flex items-center gap-1 cursor-pointer mt-1 font-medium"
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                          </div>
                            )
                          : <span className="text-gray-600">-</span>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingItem && (
        <SimulationItemEditor
          suiteId={suite.id}
          item={editingItem === 'new' ? null : editingItem}
          onSave={handleItemSaved}
          onClose={() => setEditingItem(null)}
        />
      )}

      {viewingConversation && (
        <SimulationConversationViewer
          item={viewingConversation.item}
          targetId={viewingConversation.targetId}
          initialRunIndex={viewingConversation.initialRunIndex}
          onClose={() => setViewingConversation(null)}
        />
      )}
    </div>
  )
}
